import React from 'react';
import { useI18n } from '../../context/I18nContext';
import { MacroTessera } from './MacroTessera';
import CableTessera from './CableTessera';
import { validateCable } from '../../utils/validation';
import { getCableDisplayName } from '../../utils/cableLogic';
import WarningIcon from '../ui/WarningIcon';

export const NestedMacroTessera = ({
  groupName,
  groupData,
  onCableClick,
  onEditCable,
  onEditMultipolarCable,
  onDeleteCable,
  onDeleteMultipolarCable,
  settings,
  isDragEnabled,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  draggedIndex,
  dragOverIndex,
  editingIndex,
  editingMultipolarIndex,
  showDeleteButtons,
  onToggleDelete,
  getCurrentGroupTitle,
  handleGroupTitleChange,
  handleGroupTitleBlur,
  handleGroupTitleKeyDown,
  editingGroupTitle,
  groupSortedCablesForList,
  setScrollToCable, 
  setHoveredCable, 
  isSelectionModeEnabled,
  selectedCables,
  onToggleSelection,
  availableSheaths = []
}) => {
  const { t } = useI18n();
  const { subGroups, totalCables } = groupData;

  const hasIssues = subGroups.some(subGroup =>
    subGroup.cables.some(cable => {
      const validation = validateCable(cable, groupData.allCables, settings.lingua);
      return !validation.isValid;
    })
  );

  return (
    <div className={`border rounded-lg p-4 transition-colors ${
      hasIssues
        ? 'bg-amber-50 border-amber-200 hover:bg-amber-100'
        : 'bg-purple-50 border-purple-200 hover:bg-purple-100'
    }`}>
      {/* Header del gruppo forzato */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1 mr-4">
          <div className="flex items-center gap-3">
            {/* Checkbox per selezione del gruppo principale */}
            {isSelectionModeEnabled && (
              <label className="flex items-center cursor-pointer flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={groupData.allCables.some(cable => selectedCables.has(cable.originalIndex))}
                  onChange={() => {
                    // Se tutti i cavi del gruppo sono selezionati, deseleziona tutti
                    // Altrimenti seleziona tutti
                    const allSelected = groupData.allCables.every(cable => selectedCables.has(cable.originalIndex));
                    groupData.allCables.forEach(cable => {
                      if (allSelected) {
                        onToggleSelection(cable.originalIndex);
                      } else if (!selectedCables.has(cable.originalIndex)) {
                        onToggleSelection(cable.originalIndex);
                      }
                    });
                  }}
                  className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                />
              </label>
            )}
            
            {/* Campo titolo editabile */}
            <input
              type="text"
              value={getCurrentGroupTitle(groupName)}
              onChange={(e) => handleGroupTitleChange(groupName, e.target.value)}
              onBlur={() => handleGroupTitleBlur(groupName)}
              onKeyDown={(e) => handleGroupTitleKeyDown(e, groupName)}
              onFocus={(e) => {
                if (editingGroupTitle[groupName] === undefined) {
                  const currentDisplayed = e.target.value;
                  handleGroupTitleChange(groupName, currentDisplayed);
                }
              }}
              placeholder="Inserisci titolo descrittivo del gruppo..."
              className="w-full px-3 py-2 text-base font-semibold border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
            />
          </div>
        </div>

        {/* Info + Badge allineati */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Info cavi */}
          <div className="text-sm text-gray-600 text-right">
            {totalCables === 1 ? `1 ${t('cableList.cable')}` : `${totalCables} ${t('cableList.cables')}`}
            {` ${t('cableList.inSubgroups', { count: subGroups.length, word: subGroups.length === 1 ? t('cableList.subgroup') : t('cableList.subgroups') })}`}
          </div>

          {/* Badge gruppo */}
          <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
            {groupName}
          </div>

          {/* Badge warning se ci sono problemi */}
          {hasIssues && (
            <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
              ⚠️ {t('cableList.hasProblems')}
            </span>
          )}
        </div>
      </div>

          {/* Lista dei sottogruppi */}
          <div className="space-y-3 pl-4 border-l-2 border-purple-300">
            {groupSortedCablesForList(groupData.allCables).map((group, groupIndex) => {
          if (group.type === 'single' || group.cables.length === 1) {
            // Sottogruppo singolo - usa CableTessera normale
            const cable = group.cables[0];
            const isGlobalDragging = draggedIndex === cable.originalIndex;
            const isGlobalDragOver = dragOverIndex === cable.originalIndex && draggedIndex !== cable.originalIndex;
            const validation = validateCable(cable, groupData.allCables, settings.lingua);
            const hasInnerIssues = !validation.isValid;

            return (
              <div
                key={`subgroup-single-${groupIndex}-${cable.originalIndex}`}
                className="relative"
                draggable={isDragEnabled}
                onDragStart={(e) => handleDragStart(e, cable.originalIndex)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, cable.originalIndex)}
                onDragEnter={(e) => handleDragEnter(e, cable.originalIndex)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, cable.originalIndex)}
                style={{
                  transform: isGlobalDragging ? 'scale(0.95)' : isGlobalDragOver ? 'translateY(-1px)' : 'none',
                  boxShadow: isGlobalDragOver ? '0 4px 8px rgba(59, 130, 246, 0.3)' :
                    hasInnerIssues ? '0 2px 4px rgba(245, 158, 11, 0.2)' : 'none'
                }}
              >

              {/* Icone drag/warning */}
              {isDragEnabled && (
                <div className={`absolute left-4 text-gray-400 hover:text-blue-600 transition-colors z-10 ${
                  cable.poli && Array.isArray(cable.poli) 
                    ? 'top-7'
                    : 'top-1/2 transform -translate-y-1/2'
                }`}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM17 14a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
                  </svg>
                </div>
              )}

              {!isDragEnabled && hasInnerIssues && (
                <div className={`absolute left-4 z-10 ${
                  cable.poli && Array.isArray(cable.poli) 
                    ? 'top-7'
                    : 'top-1/2 transform -translate-y-1/2'
                }`}>
                  <WarningIcon
                    issues={validation.errors}
                    className="flex-shrink-0"
                  />
                </div>
              )}
                <CableTessera
                  cable={cable}
                  validateCable={(c) => validateCable(c, groupData.allCables, settings.lingua)}
                  displaySiglatura={getCableDisplayName(cable, settings.lingua, settings)}
                  onCableClick={(siglatura, originalIndex) => {
                    if (!isDragEnabled && !isSelectionModeEnabled) {
                      setScrollToCable({
                        siglatura: siglatura,
                        originalIndex: originalIndex
                      });
                      setHoveredCable(originalIndex);
                    }
                  }}
                  onEditCable={onEditCable}
                  onEditMultipolarCable={onEditMultipolarCable}
                  onDeleteCable={onDeleteCable}
                  onDeleteMultipolarCable={onDeleteMultipolarCable}
                  showSiglatura={true}
                  clickable={!isDragEnabled && !isSelectionModeEnabled}
                  settings={settings}
                  isDragEnabled={isDragEnabled}
                  isDragging={isGlobalDragging}
                  isDragOver={isGlobalDragOver}
                  hasIssues={hasInnerIssues}
                  editingIndex={editingIndex}
                  editingMultipolarIndex={editingMultipolarIndex}
                  availableSheaths={availableSheaths}
                  showDeleteButton={showDeleteButtons && showDeleteButtons.has(cable.originalIndex)}
                  onToggleDelete={() => onToggleDelete && onToggleDelete(cable.originalIndex)}
                  isSelectionModeEnabled={isSelectionModeEnabled}
                  selectedCables={selectedCables}
                  onToggleSelection={onToggleSelection}
                />
              </div>
            );
          } else {
            // Sottogruppo multiplo - usa MacroTessera normale
            const firstCable = group.cables[0];
            const displaySiglatura = getCableDisplayName(firstCable, settings.lingua, settings);

            return (
              <MacroTessera
                key={`subgroup-multi-${groupIndex}`}
                siglatura={displaySiglatura}
                groupData={{
                  cables: group.cables,
                  isMultiple: true,
                  // Verifica se è un gruppo guaina
                  isSheathGroup: group.cables.length > 0 && group.cables[0].guaina && group.cables.every(c => c.guaina === group.cables[0].guaina),
                  sheathId: group.cables.length > 0 && group.cables[0].guaina ? group.cables[0].guaina : null
                }}
                onCableClick={onCableClick}
                onEditCable={onEditCable}
                onEditMultipolarCable={onEditMultipolarCable}
                onDeleteCable={onDeleteCable}
                onDeleteMultipolarCable={onDeleteMultipolarCable}
                settings={settings}
                isDragEnabled={isDragEnabled}
                displaySiglatura={displaySiglatura}
                availableSheaths={availableSheaths}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
                handleDragOver={handleDragOver}
                handleDragEnter={handleDragEnter}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                draggedIndex={draggedIndex}
                dragOverIndex={dragOverIndex}
                editingIndex={editingIndex}
                editingMultipolarIndex={editingMultipolarIndex}
                showDeleteButtons={showDeleteButtons}
                onToggleDelete={onToggleDelete}
                isSelectionModeEnabled={isSelectionModeEnabled}
                selectedCables={selectedCables}
                onToggleSelection={onToggleSelection}
              />
            );
          }
        })}
      </div>
    </div>
  );
};
